import { useState } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { api } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/useToast';
import { getErrorMessage, getFieldErrors } from '../../lib/errorUtils';

type ContactSupportModalProps = {
  open: boolean;
  onClose: () => void;
};

export function ContactSupportModal({ open, onClose }: ContactSupportModalProps) {
  const user = useAuthStore((state) => state.user);
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    setFieldErrors({});

    const errors: Record<string, string> = {};
    
    if (formData.subject.trim().length < 5 || formData.subject.trim().length > 200) {
      errors.subject = 'Subject must be between 5 and 200 characters.';
    }
    if (formData.message.trim().length < 10 || formData.message.trim().length > 2000) {
      errors.message = 'Message must be between 10 and 2000 characters.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrorMessage('Please fix the errors below.');
      setStatus('error');
      return;
    }

    try {
      await api.post('/support', {
        name: user ? `${user.firstName} ${user.lastName}` : 'Anonymous User',
        email: user?.email || 'no-email@mopion.com',
        subject: formData.subject,
        message: formData.message,
      });
      setStatus('success');
      setFormData({ subject: '', message: '' });
      showToast('Support message sent successfully', 'success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
      }, 2000);
    } catch (error: any) {
      setStatus('error');
      const errors = getFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setErrorMessage('Please fix the errors below.');
      } else {
        const message = getErrorMessage(error, 'Failed to send message. Please try again.');
        setErrorMessage(message);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClose = () => {
    if (status !== 'loading') {
      setFormData({ subject: '', message: '' });
      setStatus('idle');
      setErrorMessage('');
      setFieldErrors({});
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Contact Support">
      {status === 'success' ? (
        <div className="space-y-4 py-8 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-tertiary/20">
            <span className="material-symbols-outlined text-3xl text-primary">check_circle</span>
          </div>
          <h3 className="text-xl font-bold text-primary">Message sent!</h3>
          <p className="text-on-surface/70">
            Our team will get back to you within 24 hours.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <p className="text-sm text-on-surface/70">
              Need help with the platform? Send us a message and we'll respond within 24 hours during business days.
            </p>

            {user && (
              <div className="rounded-lg bg-surface-container p-4">
                <p className="text-sm">
                  <strong>Sending as:</strong> {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-on-surface/70">{user.email}</p>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="subject" className="label-sm text-on-surface">
                Subject
              </label>
              <Input
                id="subject"
                name="subject"
                type="text"
                required
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief description of your issue"
                disabled={status === 'loading'}
              />
              {fieldErrors.subject && (
                <p className="text-xs text-error">{fieldErrors.subject}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="message" className="label-sm text-on-surface">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="Please describe your issue in detail..."
                disabled={status === 'loading'}
                rows={6}
                className="w-full rounded-lg border border-outline-variant bg-surface px-4 py-3 text-on-surface transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
              {fieldErrors.message && (
                <p className="text-xs text-error">{fieldErrors.message}</p>
              )}
            </div>
          </div>

          {status === 'error' && (
            <div className="rounded-lg bg-error-container p-4">
              <p className="text-sm font-medium text-on-error-container">{errorMessage}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={status === 'loading'}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={status === 'loading'}
              loading={status === 'loading'}
              className="flex-1"
            >
              {status === 'loading' ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
